import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import type { ContestType } from '@betterpool/api-types/contests-service';

import { useLeagues } from '~/domains/common/hooks/useLeagues';
import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import type { ContestStatus } from '~/domains/contest/domains/services/enums/contests-status.enum';
import { UserContestAssociation } from '~/domains/contest/domains/services/enums/user-contest-association.enum';
import {
  getCommissionerContestsProfileV2,
  SPLASH_CONTEST_SOURCES_V2,
} from '~/services/contests/contests-v2.service.api';
import type { GetCommissionerContestsProfileV2Params } from '~/services/contests/contests-v2.service.api';
import { STALE_TIME } from '~/domains/contest/constants/query';
import { isLoadingFixedForDisabledQuery } from '~/domains/common/utils/isLoadingFixedForEnabled';
import { ChannelContestFilter } from '~/domains/channel/enums';

import parseCommissionerContestsProfileV2 from './parseCommissionerContestsProfileV2';

const PAGE_SIZE = 20;

const getStatusesForFilter = (channelContestFilter: ChannelContestFilter): string =>
  match(channelContestFilter)
    .with(ChannelContestFilter.ALL, () => 'scheduled,in_progress,finalized')
    .with(ChannelContestFilter.NOT_YET_STARTED, () => 'scheduled')
    .with(ChannelContestFilter.STARTED, () => 'in_progress')
    .with(ChannelContestFilter.FINAL, () => 'finalized')
    .exhaustive();

const getStatusFilter = ({
  contestStatus,
  channelContestFilter,
}: {
  contestStatus?: ContestStatus;
  channelContestFilter?: ChannelContestFilter;
}): GetCommissionerContestsProfileV2Params['status'] => {
  if (contestStatus) {
    return contestStatus.toLowerCase();
  }

  return channelContestFilter ? getStatusesForFilter(channelContestFilter) : undefined;
};

export const useChannelContestsProfileV2 = ({
  userId,
  channelContestFilter,
  contestStatus,
  association,
  enabled = true,
}: {
  userId: string;
  channelContestFilter?: ChannelContestFilter;
  contestStatus?: ContestStatus;
  association?: UserContestAssociation;
  enabled?: boolean;
}) => {
  const router = useRouter();
  const currentQuery = { ...router.query };
  const { data: leagues, isLoading: isLeaguesLoading } = useLeagues();

  const URLContestStatusFilter = currentQuery[URLQueryKeys.CONTEST_STATUS] as ContestStatus;
  const URLLeagueFilter = currentQuery[URLQueryKeys.LEAGUES] as string;
  const URLContestTypeFilter = currentQuery[URLQueryKeys.CONTEST_TYPE] as ContestType;
  const leagueFilter = URLLeagueFilter
    ? leagues?.find((league) => league.alias === URLLeagueFilter)?.alias
    : undefined;
  const URLAssociationFilter =
    (currentQuery[URLQueryKeys.ASSOCIATION] as UserContestAssociation) ||
    association ||
    UserContestAssociation.CREATED;
  const URLRestartStatusFilter = currentQuery[URLQueryKeys.RESTART_STATUS] as string;

  const tab: GetCommissionerContestsProfileV2Params['tab'] =
    URLAssociationFilter === UserContestAssociation.ENTERED ? 'entered' : 'created';

  const effectiveStatus = contestStatus ?? URLContestStatusFilter;
  const status = getStatusFilter({ contestStatus: effectiveStatus, channelContestFilter });

  const queryKey = [
    'channel/contests/profile/v2',
    userId,
    tab,
    status,
    URLLeagueFilter,
    URLContestTypeFilter,
    URLRestartStatusFilter,
    leagueFilter,
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    fetchStatus,
    isLoading,
    isFetching,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    enabled: enabled && !!userId && (!URLLeagueFilter || !!leagueFilter || !isLeaguesLoading),
    keepPreviousData: true,
    queryFn: async ({ pageParam = 0 }) =>
      getCommissionerContestsProfileV2({
        userId,
        tab,
        status,
        league: leagueFilter,
        source: SPLASH_CONTEST_SOURCES_V2,
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const nextPage = lastPage.offset + PAGE_SIZE;
      if (nextPage > lastPage.total) {
        return undefined;
      }
      return nextPage;
    },
    staleTime: STALE_TIME,
  });

  const contests = useMemo(
    () => parseCommissionerContestsProfileV2(data?.pages?.flatMap((page) => page.data) ?? []),
    [data?.pages]
  );

  return {
    queryKey,
    contests,
    totalCount: data?.pages[0]?.total,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingFixedForDisabledQuery(isLoading, fetchStatus),
    isFetching: isFetching && !isFetchingNextPage,
  };
};
